import type { TreeNavNode } from './TreeNav'

/** Дерево источника как данные: узел знает детей, но не знает, свёрнут ли он, — это намерение человека. */
export interface TreeSource {
  id: string
  label: string
  caption?: string | null
  badge?: string | null
  icon?: TreeNavNode['icon']
  children?: readonly TreeSource[]
}

/**
 * Разворачивает дерево в плоский список видимых строк для `TreeNav`. Свёрнутые узлы (`collapsed`) скрывают детей;
 * узел без детей не раскрывается. Выбранный — `selectedId`.
 */
export function flattenTree(roots: readonly TreeSource[], collapsed: readonly string[], selectedId: string | null): TreeNavNode[] {
  const closed = new Set(collapsed)
  const rows: TreeNavNode[] = []
  const walk = (nodes: readonly TreeSource[], depth: number) => {
    for (const node of nodes) {
      const children = node.children ?? []
      const expandable = children.length > 0
      const expanded = expandable && !closed.has(node.id)
      rows.push({
        id: node.id,
        label: node.label,
        caption: node.caption ?? null,
        depth,
        expandable,
        expanded,
        selected: node.id === selectedId,
        badge: node.badge ?? null,
        ...(node.icon !== undefined ? { icon: node.icon } : {}),
      })
      if (expanded) walk(children, depth + 1)
    }
  }
  walk(roots, 0)
  return rows
}

/** Ключи всех узлов с детьми под `id` и сам `id`, если он найден; `[]` — такого узла нет. Нужно, чтобы выбрать «всё внутри». */
export function descendantsOf(roots: readonly TreeSource[], id: string): readonly string[] {
  const found: string[] = []
  const collect = (node: TreeSource) => {
    found.push(node.id)
    for (const child of node.children ?? []) collect(child)
  }
  const find = (nodes: readonly TreeSource[]): boolean => {
    for (const node of nodes) {
      if (node.id === id) {
        collect(node)
        return true
      }
      if (find(node.children ?? [])) return true
    }
    return false
  }
  find(roots)
  return found
}
